import { cn } from "@/lib/utils";
import { planLogLineTone } from "@/lib/planLogTones";
import {
  CANDIDATE_JOB_LABEL,
  CANDIDATE_LOG_LINES,
  CORPUS_LOG_LINES,
  EXCERPT_START_LINE,
  LOG_EXCERPT_LINES,
  NEEDLE_LINE,
} from "@/lib/homepageLogExcerpt";

/**
 * The before→after "haystack" strip: a real terraform plan log crawling past
 * (verbatim from the live demo's flagged candidate run, with true absolute
 * line numbers) against the one row DeployCompare reduces it to. Sits directly
 * above HomepageDemoPreview so the argument hands off to the evidence.
 */

const LINE_HEIGHT_PX = 19;

function LogLine({ line, absoluteNumber }: { line: string; absoluteNumber: number }) {
  const tone = planLogLineTone(line);
  return (
    <div
      className={cn("flex", tone === "needle" && "bg-warning/10")}
      style={{ height: LINE_HEIGHT_PX }}
    >
      <span className="w-12 shrink-0 select-none pr-3 text-right tabular-nums text-muted-foreground/40">
        {absoluteNumber}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 overflow-hidden whitespace-pre",
          tone === "muted" && "text-muted-foreground/80",
          tone === "marker" && "text-foreground",
          tone === "needle" && "font-semibold text-warning"
        )}
      >
        {line || " "}
      </span>
    </div>
  );
}

export function HomepageHaystack() {
  // Under prefers-reduced-motion the crawl freezes; park the window so the
  // needle line sits a third of the way down instead of on the excerpt's top.
  const needleIndex = NEEDLE_LINE - EXCERPT_START_LINE;
  const staticOffsetPx = Math.max(0, needleIndex * LINE_HEIGHT_PX - 112);

  return (
    <section
      aria-labelledby="haystack-heading"
      className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-14"
    >
      <p className="sr-only">
        Example of the manual alternative: a {CANDIDATE_LOG_LINES}-line terraform plan log from the
        demo&apos;s data-platform stack, where the replaced RDS cluster appears on line{" "}
        {NEEDLE_LINE}. The full review window holds {CORPUS_LOG_LINES.toLocaleString("en-US")} lines
        of plan output.
      </p>

      <figure
        aria-hidden="true"
        className="home-log-window min-w-0 overflow-hidden rounded-xl log-surface ring-1 ring-border"
      >
        <figcaption className="flex items-baseline justify-between gap-3 border-b border-border/70 px-4 py-2.5 font-mono text-xs">
          <span className="truncate text-muted-foreground">{CANDIDATE_JOB_LABEL}</span>
          <span className="shrink-0 text-muted-foreground/70">{CANDIDATE_LOG_LINES} lines</span>
        </figcaption>
        <div className="home-log-mask relative h-[300px] overflow-hidden sm:h-[360px] lg:h-[430px]">
          <div
            className="home-log-crawl font-mono text-[12px]"
            style={{ "--home-log-static": `-${staticOffsetPx}px` } as React.CSSProperties}
          >
            {[0, 1].map((copy) => (
              <div key={copy}>
                {LOG_EXCERPT_LINES.map((line, index) => (
                  <LogLine key={index} line={line} absoluteNumber={EXCERPT_START_LINE + index} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </figure>

      <div className="min-w-0">
        <h2
          id="haystack-heading"
          className="max-w-xl text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
        >
          The needle is on line {NEEDLE_LINE}.
        </h2>
        <p className="mt-5 max-w-xl text-pretty text-base leading-7 text-muted-foreground">
          A replaced database cluster hides in {CANDIDATE_LOG_LINES} lines of routine plan output.
          This project&apos;s review window holds {CORPUS_LOG_LINES.toLocaleString("en-US")} lines
          of it. DeployCompare reads every run against the stack&apos;s recent successes and hands
          you the row that broke pattern:
        </p>
        <dl className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-border py-4 text-sm">
          <div className="inline-flex items-center gap-2 font-semibold text-warning">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-warning" />
            Investigate
          </div>
          <div className="flex items-baseline gap-2">
            <dt className="sr-only">Stack</dt>
            <dd className="font-mono font-semibold text-foreground">data-platform</dd>
          </div>
          <div className="flex items-baseline gap-2">
            <dt className="text-muted-foreground">plan</dt>
            <dd className="font-mono text-foreground">+2 ~2 -3 R1</dd>
          </div>
          <div className="flex items-baseline gap-2">
            <dt className="text-muted-foreground">score</dt>
            <dd className="font-mono text-foreground">
              3.72 <span aria-hidden="true">→</span>
              <span className="sr-only">to</span>{" "}
              <span className="font-semibold text-warning">14.72</span>
            </dd>
          </div>
        </dl>
        <p className="mt-4 text-sm leading-6 text-muted-foreground">
          Real output from the live demo&apos;s{" "}
          <span className="font-mono text-foreground/90">data-platform</span> stack. You can open
          the same comparison below.
        </p>
      </div>
    </section>
  );
}
